// ================================================================
// 使用者管理頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-21
// 功能：使用者列表、搜尋、操作
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Block,
  CheckCircle,
  AccountBalanceWallet,
  Visibility,
  Refresh,
} from '@mui/icons-material';
import {
  getUserList,
  updateUserStatus,
  adjustUserBalance,
  UserListItem,
  Pagination,
} from '../../api/admin';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';

// ================================================================
// 主要組件
// ================================================================

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin } = useAdmin();

  // 狀態
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // 操作選單
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);

  // 對話框狀態
  const [statusDialog, setStatusDialog] = useState(false);
  const [balanceDialog, setBalanceDialog] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceReason, setBalanceReason] = useState('');
  const [balanceOperation, setBalanceOperation] = useState<'add' | 'deduct'>('add');
  const [adjustType, setAdjustType] = useState<'balance' | 'points'>('balance');
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // 載入使用者列表
  const loadUsers = useCallback(async (page = 1, search = '') => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getUserList(page, pagination.page_size, search);
      // 後端直接回傳 response.users，不是 response.data.users
      const usersData = (response as any).users || response.data?.users;
      const paginationData = (response as any).pagination || response.data?.pagination;
      if (response.success && usersData) {
        setUsers(usersData);
        if (paginationData) {
          setPagination(paginationData);
        }
      } else {
        setError(response.message || '無法載入使用者列表');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page_size]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // 搜尋處理
  const handleSearch = () => {
    loadUsers(1, searchQuery);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 分頁處理
  const handleChangePage = (_: unknown, newPage: number) => {
    loadUsers(newPage + 1, searchQuery);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({ ...prev, page_size: parseInt(event.target.value, 10) }));
    loadUsers(1, searchQuery);
  };

  // 操作選單處理
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserListItem) => {
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // 狀態變更處理
  const handleStatusChange = async () => {
    if (!selectedUser) return;

    setIsProcessing(true);
    try {
      // 判斷當前狀態：is_suspended = 0 表示活躍，需要變更為 suspended
      const isCurrentlyActive = !selectedUser.is_suspended || selectedUser.is_suspended === 0;
      const newStatus = isCurrentlyActive ? 'suspended' : 'active';
      const response = await updateUserStatus(selectedUser.user_id, newStatus);
      if (response.success) {
        const displayName = selectedUser.full_name || selectedUser.email || '未知';
        setActionSuccess(`使用者 ${displayName} 已${newStatus === 'active' ? '啟用' : '停用'}`);
        loadUsers(pagination.page, searchQuery);
      } else {
        setError(response.message || '操作失敗');
      }
    } catch (err: any) {
      setError(err.message || '操作失敗');
    } finally {
      setIsProcessing(false);
      setStatusDialog(false);
      setSelectedUser(null);
    }
  };

  // 餘額調整處理
  const handleBalanceAdjust = async () => {
    if (!selectedUser || !balanceAmount) return;

    setIsProcessing(true);
    try {
      const rawAmount = parseFloat(balanceAmount);
      if (isNaN(rawAmount) || rawAmount === 0) {
        setError('請輸入有效的金額（不可為零）');
        setIsProcessing(false);
        return;
      }

      // 使用絕對值，根據操作類型決定正負號
      const absAmount = Math.abs(rawAmount);
      const amount = balanceOperation === 'deduct' ? -absAmount : absAmount;

      const response = await adjustUserBalance(selectedUser.user_id, amount, balanceReason, adjustType);
      if (response.success) {
        const displayName = selectedUser.full_name || selectedUser.email || '未知';
        const operationText = balanceOperation === 'add' ? '加值' : '扣款';
        const typeText = adjustType === 'balance' ? '餘額' : '點數';
        setActionSuccess(`使用者 ${displayName} ${typeText}${operationText}成功：${amount >= 0 ? '+' : ''}${amount}`);
        loadUsers(pagination.page, searchQuery);
      } else {
        setError(response.message || '操作失敗');
      }
    } catch (err: any) {
      setError(err.message || '操作失敗');
    } finally {
      setIsProcessing(false);
      setBalanceDialog(false);
      setBalanceAmount('');
      setBalanceReason('');
      setBalanceOperation('add');
      setAdjustType('balance');
      setSelectedUser(null);
    }
  };

  // 檢查是否有餘額調整權限
  const canAdjustBalance = admin?.role === 'super_admin' || admin?.role === 'admin';

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" fontWeight="bold">
            使用者管理
          </Typography>
          <Tooltip title="重新整理">
            <IconButton onClick={() => loadUsers(pagination.page, searchQuery)}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 成功訊息 */}
        {actionSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
            {actionSuccess}
          </Alert>
        )}

        {/* 錯誤訊息 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 搜尋區域 */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box display="flex" gap={2}>
            <TextField
              fullWidth
              placeholder="搜尋使用者名稱、Email 或手機..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" onClick={handleSearch}>
              搜尋
            </Button>
          </Box>
        </Paper>

        {/* 使用者列表 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>使用者</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>餘額</TableCell>
                  <TableCell>查詢次數</TableCell>
                  <TableCell>狀態</TableCell>
                  <TableCell>註冊時間</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">沒有找到使用者</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => {
                    // 判斷用戶狀態：is_suspended = 0 表示活躍，= 1 表示停用
                    const isActive = !user.is_suspended || user.is_suspended === 0;
                    return (
                      <TableRow key={user.user_id} hover>
                        <TableCell>
                          <Typography fontWeight="bold">{user.full_name || user.email || '-'}</Typography>
                        </TableCell>
                        <TableCell>{user.email || '-'}</TableCell>
                        <TableCell>
                          <Typography
                            color={user.balance > 0 ? 'success.main' : 'text.secondary'}
                            fontWeight="bold"
                          >
                            ${user.balance?.toFixed(2) || '0.00'}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.query_count || 0}</TableCell>
                        <TableCell>
                          <Chip
                            label={isActive ? '活躍' : '停用'}
                            color={isActive ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(user.created_at).toLocaleDateString('zh-TW')}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton onClick={(e) => handleMenuOpen(e, user)}>
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 分頁 */}
          <TablePagination
            component="div"
            count={pagination.total}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={pagination.page_size}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="每頁顯示"
          />
        </Paper>

        {/* 操作選單 */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              if (selectedUser) {
                navigate(`/admin/users/${selectedUser.user_id}`);
              }
            }}
          >
            <Visibility sx={{ mr: 1 }} fontSize="small" />
            查看詳情
          </MenuItem>
          {canAdjustBalance && (
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setBalanceDialog(true);
              }}
            >
              <AccountBalanceWallet sx={{ mr: 1 }} fontSize="small" />
              調整餘額
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              handleMenuClose();
              setStatusDialog(true);
            }}
          >
            {(!selectedUser?.is_suspended || selectedUser?.is_suspended === 0) ? (
              <>
                <Block sx={{ mr: 1 }} fontSize="small" />
                停用帳號
              </>
            ) : (
              <>
                <CheckCircle sx={{ mr: 1 }} fontSize="small" />
                啟用帳號
              </>
            )}
          </MenuItem>
        </Menu>

        {/* 狀態變更對話框 */}
        <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
          <DialogTitle>
            {(!selectedUser?.is_suspended || selectedUser?.is_suspended === 0) ? '停用帳號' : '啟用帳號'}
          </DialogTitle>
          <DialogContent>
            <Typography>
              確定要{(!selectedUser?.is_suspended || selectedUser?.is_suspended === 0) ? '停用' : '啟用'}使用者「
              {selectedUser?.full_name || selectedUser?.email || '未知'}」的帳號嗎？
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialog(false)}>取消</Button>
            <Button
              variant="contained"
              color={(!selectedUser?.is_suspended || selectedUser?.is_suspended === 0) ? 'error' : 'success'}
              onClick={handleStatusChange}
              disabled={isProcessing}
            >
              {isProcessing ? <CircularProgress size={20} /> : '確定'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 餘額/點數調整對話框 */}
        <Dialog open={balanceDialog} onClose={() => setBalanceDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>調整餘額 / 點數</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Typography gutterBottom>
                使用者：{selectedUser?.full_name || selectedUser?.email || '未知'}
              </Typography>
              <Typography gutterBottom color="text.secondary">
                目前餘額：${selectedUser?.balance?.toFixed(2) || '0.00'}
              </Typography>

              {/* 調整項目選擇 */}
              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  調整項目
                </Typography>
                <ToggleButtonGroup
                  value={adjustType}
                  exclusive
                  onChange={(_, value) => value && setAdjustType(value)}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="balance" color="primary">
                    現金餘額
                  </ToggleButton>
                  <ToggleButton value="points" color="secondary">
                    體驗點數
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* 操作類型選擇 */}
              <Box sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  操作類型
                </Typography>
                <ToggleButtonGroup
                  value={balanceOperation}
                  exclusive
                  onChange={(_, value) => value && setBalanceOperation(value)}
                  fullWidth
                >
                  <ToggleButton value="add" color="success">
                    加值 (+)
                  </ToggleButton>
                  <ToggleButton value="deduct" color="error">
                    扣款 (-)
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <TextField
                fullWidth
                label={adjustType === 'balance' ? '金額（NTD）' : '點數'}
                type="number"
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
                margin="normal"
                inputProps={{ min: 0, step: "any" }}
                helperText={
                  adjustType === 'balance'
                    ? (balanceOperation === 'add' ? '將增加此金額到現金餘額' : '將從現金餘額扣除此金額')
                    : (balanceOperation === 'add' ? '將增加此點數到體驗點數（有效期延長30天）' : '將從體驗點數扣除')
                }
              />
              <TextField
                fullWidth
                label="調整原因"
                value={balanceReason}
                onChange={(e) => setBalanceReason(e.target.value)}
                margin="normal"
                multiline
                rows={2}
                required
                helperText="請填寫調整原因（必填）"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBalanceDialog(false)}>取消</Button>
            <Button
              variant="contained"
              color={balanceOperation === 'add' ? 'success' : 'error'}
              onClick={handleBalanceAdjust}
              disabled={isProcessing || !balanceAmount || !balanceReason}
            >
              {isProcessing ? <CircularProgress size={20} /> : (balanceOperation === 'add' ? '確定加值' : '確定扣款')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default UserManagementPage;
